// Reine Logik der Arbeitsliste (Schritt 4, ADR 0243): welche Knoepfe ein Schritt hat, was auf der Karte
// steht, wie die Sperr-Meldung lautet. Ohne React, damit vitest sie prueft (vitest laeuft hier nur in
// einer Node-Umgebung, ohne Komponenten-Render).
import { companyMatchesDomain, stripLegalSuffix } from '../fraud0/lib/linkedin';
import type { ArbeitslisteSchritt } from '../../services/leadGenApi';

/** Die Schritt-Arten, die der Ticket-Automat vergibt (`config.LINKEDIN_KANAELE`). */
export type SchrittKanal =
  | 'li_anfrage' | 'li_annahme_pruefen' | 'li_anschreiben' | 'li_nachfassen' | 'li_antworten'
  | 'li_aufwaermen';

export const SCHRITT_TITEL: Record<SchrittKanal, string> = {
  li_anfrage: 'Freundschaftsanfrage schicken',
  li_annahme_pruefen: 'Prüfen: angenommen?',
  li_anschreiben: 'Erste Nachricht schreiben',
  li_nachfassen: 'Nachfassen',
  li_antworten: 'Antwort abschicken',
  li_aufwaermen: 'Aufwärmen',
};

/** Der Knopf-Satz je Schritt. `composer` = Pitch-/Follow-up-Fenster, `ergebnis` = Annahme-Auswahl,
 *  `antwort` = Text des Besitzers kopieren und „gesendet“. „Zurücklegen“ gibt es immer. */
export type KarteAktion = 'anfrage_composer' | 'ergebnis_waehlen' | 'pitch_composer' | 'followup_composer'
  | 'antwort_senden' | 'aufwaermen';

export function karteAktion(kanal: string | null | undefined): KarteAktion | null {
  switch (kanal) {
    case 'li_anfrage': return 'anfrage_composer';
    case 'li_annahme_pruefen': return 'ergebnis_waehlen';
    case 'li_anschreiben': return 'pitch_composer';
    case 'li_nachfassen': return 'followup_composer';
    case 'li_antworten': return 'antwort_senden';
    case 'li_aufwaermen': return 'aufwaermen';
    default: return null;
  }
}

/** Die drei Ergebnisse, die „Annahme prüfen“ anbietet (Vokabular `services/sheets_ergebnisse`). */
export const ANNAHME_ERGEBNISSE: { wert: 'angenommen' | 'geghostet' | 'blockiert'; label: string }[] = [
  { wert: 'angenommen', label: 'Angenommen' },
  { wert: 'geghostet', label: 'Geghostet' },
  { wert: 'blockiert', label: 'Blockiert' },
];

export interface KontoRest {
  id: number; name: string | null; rest: number; tag_rest: number; woche_rest: number; voll: boolean;
}

/** „Konto 2: noch 14 · Konto 5: voll“ — eine Zeile, in der Reihenfolge des Servers. */
export function kontoZeile(konten: KontoRest[]): string {
  if (!konten.length) return '';
  return konten.map((k) => `${k.name ?? `Konto ${k.id}`}: ${k.voll ? 'voll' : `noch ${k.rest}`}`).join(' · ');
}

/** Die Kontozeile einer Karte (ADR 0328): nur das Konto des Schritts, mit Tages- und Wochenrest
 *  („Manfred C. · heute noch 8, Woche noch 25“). Ohne Konto am Schritt (oder unbekanntes Konto) die ganze Zeile. */
export function kontoZeileFuer(konten: KontoRest[], kontoId: number | null | undefined): string {
  if (kontoId == null) return kontoZeile(konten);
  const k = konten.find((x) => x.id === kontoId);
  if (!k) return kontoZeile(konten);
  const name = k.name ?? `Konto ${k.id}`;
  return k.voll ? `${name} · voll` : `${name} · heute noch ${k.tag_rest}, Woche noch ${k.woche_rest}`;
}

/** LinkedIn-Seite „Gesendete Anfragen“ (ADR 0328): dort sieht die Hilfe, ob eine Anfrage noch offen ist. */
export const GESENDETE_ANFRAGEN_URL = 'https://www.linkedin.com/mynetwork/invitation-manager/sent/';

/** So lange (ms) steht „Rückgängig“ nach einem „Gesendet“ (ADR 0328). */
export const RUECKGAENGIG_MS = 8000;

/** Der Hinweis, wenn keine Anfrage mehr rausgehen kann. */
export function alleVollText(alleVoll: boolean): string | null {
  return alleVoll ? 'Alle Konten voll bis Montag. Es kommen keine Anfragen mehr.' : null;
}

/** Ist das gewählte Konto voll? Ohne Wahl (oder unbekanntes Konto) sperrt nichts. */
export function gewaehltesKontoVoll(konten: KontoRest[], kontoId: number | null | undefined): boolean {
  if (kontoId == null) return false;
  return konten.find((k) => k.id === kontoId)?.voll ?? false;
}

/** Die Meldung zu einer 409-Antwort der Besitz-Sperre (`firmen_besitz.pruefen_und_beanspruchen`).
 *  `null`, wenn der Fehler keine Besitz-Sperre ist. */
export function besitzSperreText(detail: Record<string, unknown> | undefined | null): string | null {
  if (!detail) return null;
  const grund = detail.grund;
  if (grund === 'gehoert_fraud0') return 'Diese Firma ist für fraud0 reserviert.';
  if (grund === 'fremder_besitzer') {
    const b = detail.besitzer as { name?: string } | null | undefined;
    return `Diese Firma gehört ${b?.name?.trim() || 'einem anderen Setter'}.`;
  }
  return null;
}

/** Die Meldung zu `409 {grund: "in_arbeit"}` (`_deny_in_arbeit`): die Reservierung (Standard 60 Minuten)
 *  ist abgelaufen und die Aufgabe liegt jetzt bei jemand anderem. `null`, wenn der Fehler etwas anderes ist. */
export function inArbeitText(detail: Record<string, unknown> | undefined | null): string | null {
  if (!detail || detail.grund !== 'in_arbeit') return null;
  const name = typeof detail.name === 'string' && detail.name.trim() ? detail.name.trim() : 'jemand anderem';
  return `Die Zeit für diese Aufgabe ist abgelaufen, sie liegt jetzt bei ${name}. `
    + `Schon gesendet? Dann sag ${name === 'jemand anderem' ? 'dem Kollegen' : name} Bescheid.`;
}

/** „Von Bianca übernommen“ — nur gesetzt, wenn der Server das Schild mitgibt (eine Woche). */
export function uebernommenText(von: string | null | undefined): string | null {
  return von ? `von ${von} übernommen` : null;
}

export interface ListenZeile {
  lead_id: number; domain: string; kanal: string | null; person_key: string | null;
  faellig_am: string | null; zustand: string | null; ueberfaellig: boolean;
}

const KANAL_TEXT: Record<string, string> = {
  anruf: 'Anrufen', mail_schicken: 'Mail schicken', mail_nachfassen: 'Mail nachfassen',
  text_schreiben: 'Antwort-Text schreiben',
};

/** Die Beschriftung einer Zeile in „Meine Firmen“. Unbekannte Kanäle zeigen ihren Namen, nie nichts. */
export function zeilenAktion(kanal: string | null | undefined): string {
  if (!kanal) return 'Offen';
  return KANAL_TEXT[kanal] ?? SCHRITT_TITEL[kanal as SchrittKanal] ?? kanal;
}

/** „seit 3 Tg. fällig“ / „fällig in 2 Std.“, relativ zu `jetzt` (ms). */
export function faelligText(iso: string | null | undefined, jetzt: number): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const min = Math.round((t - jetzt) / 60000);
  const a = Math.abs(min);
  const spanne = a < 60 ? `${a} Min.` : a < 1440 ? `${Math.round(a / 60)} Std.` : `${Math.round(a / 1440)} Tg.`;
  return min <= 0 ? `seit ${spanne} fällig` : `fällig in ${spanne}`;
}

/** Die kleine Zahl im Menue: leer bei 0, „9+“ ab zehn. */
export function badgeText(n: number): string {
  if (!n || n < 1) return '';
  return n > 9 ? '9+' : String(n);
}

/** Eine Person auf der Firmenkarte (ADR 0269): `dran` = die Person, die als Naechstes angerufen wird. */
export interface KartenPerson { person_key: string | null; dran?: boolean }

/**
 * Wer auf der Karte gewaehlt werden darf (ADR 0269): nur die markierte Person. Steht keine Liste an der Zeile
 * (aelterer Server, Mail-Schritt, Vorrat-Person im Schritt), bleibt es bei der Zeile des Schritts. Die Karte
 * zeigt alle, der Waehlknopf haengt nur an dem, was diese Funktion zurueckgibt.
 */
export function waehlbarePerson<T extends KartenPerson>(personen: T[] | null | undefined,
                                                        zeile: T | null | undefined): T | null {
  const dran = (personen ?? []).find((p) => p.dran);
  return dran ?? zeile ?? null;
}

/** Darf diese Person gewaehlt werden? (Wahr nur fuer `waehlbarePerson`.) */
export function darfWaehlen<T extends KartenPerson>(person: T, personen: T[] | null | undefined,
                                                    zeile: T | null | undefined): boolean {
  const w = waehlbarePerson(personen, zeile);
  return w != null && w.person_key === person.person_key;
}

/** „3 anrufbar · 10 im Vorrat“, leer ohne Zahlen. */
export function bestandText(b: { anrufbar: number; vorrat: number } | null | undefined): string {
  if (!b) return '';
  const teile = [`${b.anrufbar} anrufbar`];
  if (b.vorrat > 0) teile.push(`${b.vorrat} im Vorrat`);
  return teile.join(' · ');
}

/** Die vier Wahlen des Vorrat-Knopfs (ADR 0329, 0333) und ihre Beschriftung. */
export type VorratWahlArt = 'im_gespraech' | 'mail_raus' | 'holen' | 'nicht_holen';
export const VORRAT_WAHLEN: { wert: VorratWahlArt; label: string }[] = [
  { wert: 'im_gespraech', label: 'im Gespräch' }, { wert: 'mail_raus', label: 'Mail raus' },
  { wert: 'holen', label: 'holen' }, { wert: 'nicht_holen', label: 'nicht holen' },
];
const WAHL_LABEL = Object.fromEntries(VORRAT_WAHLEN.map((w) => [w.wert, w.label])) as Record<VorratWahlArt, string>;

/** Wie die Pille im Text der Firmenzeile aussieht: `vorschlag` gestrichelt (das System schlägt vor, noch keiner hat
 *  geklickt), `holen` blau, `warten` grau, `nein` durchgestrichen, `leer` nur Text ohne Knopf. */
export type VorratKnopfArt = 'vorschlag' | 'holen' | 'warten' | 'nein' | 'leer';
export interface VorratKnopf { text: string; art: VorratKnopfArt; wahl: VorratWahlArt | null; }

/** Der Vorrat-Knopf einer Firma. Ohne Vorrat gibt es keinen Knopf, nur den Text „kein Vorrat“. Der Server rechnet
 *  (`wirksam`: eine wartende Wahl endet mit dem Nachfass-Termin); hier wird nur beschriftet. */
export function vorratKnopf(
  b: { vorrat: number; wahl?: VorratWahlArt | null; vorschlag?: VorratWahlArt | null; wirksam?: VorratWahlArt | null } | null | undefined,
): VorratKnopf | null {
  if (!b) return null;
  if (b.vorrat <= 0) return { text: 'kein Vorrat', art: 'leer', wahl: null };
  const kopf = `${b.vorrat} im Vorrat`;
  const wahl = b.wahl ?? null;
  if (wahl === 'holen') return { text: `${kopf} · holen ▾`, art: 'holen', wahl };
  if (wahl === 'nicht_holen') return { text: `${kopf} · nicht holen ▾`, art: 'nein', wahl };
  if ((wahl === 'mail_raus' || wahl === 'im_gespraech') && b.wirksam === wahl) {
    return { text: `${kopf} · ${wahl === 'mail_raus' ? 'Mail raus, wartet' : 'im Gespräch'} ▾`, art: 'warten', wahl };
  }
  // Nichts gewählt, oder die wartende Wahl ist abgelaufen: es gilt wieder der Vorschlag des Systems.
  const v = b.vorschlag && b.vorschlag !== 'nicht_holen' ? WAHL_LABEL[b.vorschlag] : 'holen';
  return { text: `${kopf} · ${v}? ▾`, art: 'vorschlag', wahl };
}

/** Ein echter LinkedIn-Firmenlink (`linkedin.com/company/<name>`), kein Name und kein Personenprofil. */
export const istFirmenlink = (u: string | null | undefined): boolean =>
  /^https?:\/\/([a-z]{2,3}\.)?linkedin\.com\/company\/[^/?#\s]+/i.test((u ?? '').trim());

/** Aufwärmen (ADR 0320, 0345): Link zur LinkedIn-Seite der Firma. Fehlt er, eine Google-Suche nach der Seite
 *  (die Firmensuche in LinkedIn selbst liegt hinter einer Bezahl-Schranke und sucht nur, die Seite findet sie nicht). */
export function aufwaermLink(firmaUrl: string | null | undefined, domain: string | null | undefined): string | null {
  const u = (firmaUrl ?? '').trim();
  if (u) return u;
  const d = (domain ?? '').trim();
  return d ? `https://www.google.com/search?q=${encodeURIComponent(`site:linkedin.com/company "${d}"`)}` : null;
}

/** Lesbarer Name aus der Domain für die Suche (ADR 0358): ohne www und Endung, Bindestrich wird Leerzeichen.
 *  „redcare-apotheke.ch“ → „redcare apotheke“, „shop.beispiel.co.uk“ → „beispiel“. */
export function domainName(domain: string | null | undefined): string {
  const teile = (domain ?? '').trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '')
    .split('/')[0].split('.').filter(Boolean);
  if (teile.length > 1) teile.pop();                                              // Endung weg
  if (teile.length > 1 && ['co', 'com', 'org', 'net', 'ac'].includes(teile[teile.length - 1])) teile.pop();
  return (teile[teile.length - 1] ?? '').replace(/[-_]+/g, ' ').trim();
}

/** Passt der Impressum-Name zur Domain? Gleiche Regel wie in der fraud0-Karte (spiegelt das Backend). */
export const impressumPasst = (domain: string | null | undefined, name: string | null | undefined): boolean =>
  companyMatchesDomain({ domain, impressum_json: { company_name: name } } as never);

export type SuchId = 'domain' | 'firmenname' | 'linkedin' | 'bing';
export interface Suchsprosse { id: SuchId; titel: string; was: string; url: string; hinweis?: string }

const google = (q: string) => `https://www.google.com/search?q=${encodeURIComponent(q)}`;

/** Die Such-Leiter für eine Firma ohne bekannte LinkedIn-Seite (ADR 0358): erst der Name aus der Domain, dann der
 *  Firmenname aus Konzern oder Impressum (Rechtsform ab), dann LinkedIn selbst, zuletzt Bing. Keine Anführungs-
 *  zeichen: die exakte Phrase war zu streng. Fehlt ein eigener Firmenname oder ist er derselbe Suchbegriff, entfällt
 *  die zweite Sprosse. */
export function suchSprossen(
  { domain, impressumName, konzern }: { domain: string | null | undefined; impressumName?: string | null; konzern?: string | null },
): Suchsprosse[] {
  const dn = domainName(domain);
  if (!dn) return [];
  const sprossen: Suchsprosse[] = [
    { id: 'domain', titel: 'Name aus der Domain', was: `„${dn}“ auf LinkedIn-Firmenseiten`,
      url: google(`site:linkedin.com/company ${dn}`) },
  ];
  const quelle = (konzern ?? '').trim() || (impressumName ?? '').trim();
  const fn = quelle ? stripLegalSuffix(quelle) : '';
  if (fn.length >= 3 && fn.toLowerCase() !== dn.toLowerCase()) {
    const mutter = !!(konzern ?? '').trim();
    sprossen.push({
      id: 'firmenname', titel: mutter ? 'Name der Mutterfirma' : 'Firmenname aus dem Impressum', was: `„${fn}“`,
      url: google(`site:linkedin.com/company ${fn}`),
      hinweis: mutter ? 'die Seite kann unter der Mutterfirma laufen'
        : impressumPasst(domain, quelle) ? undefined : 'passt nicht zur Domain, evtl. Mutterfirma oder Dienstleister',
    });
  }
  sprossen.push(
    { id: 'linkedin', titel: 'Direkt in LinkedIn suchen', was: 'Firmensuche, du bist eingeloggt',
      url: `https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(dn)}` },
    { id: 'bing', titel: 'Bei Bing suchen', was: 'wie Suche 1, andere Suchmaschine',
      url: `https://www.bing.com/search?q=${encodeURIComponent(`site:linkedin.com/company ${dn}`)}` },
  );
  return sprossen;
}

/** Muss die Hilfe das LinkedIn-Konto wechseln (ADR 0359)? Nur wenn beide bekannt und verschieden sind. */
export const brauchtKontowechsel = (eingeloggt: number | null | undefined, gebraucht: number | null | undefined): boolean =>
  eingeloggt != null && gebraucht != null && eingeloggt !== gebraucht;

/** Konten für die Auswahl: die der Hilfe (sie ist Operatorin) zuerst, dann alle übrigen, je nach Name (ADR 0359). */
export function kontenGeordnet<T extends { id: number; name: string; owner_ids?: number[] }>(konten: T[], userId: number | null | undefined): T[] {
  const meins = (k: T) => (userId != null && (k.owner_ids ?? []).includes(userId) ? 0 : 1);
  return [...konten].sort((a, b) => meins(a) - meins(b) || a.name.localeCompare(b.name));
}

/** Steht die Kontenzeile („Manfred C. · heute noch 8“) über dieser Karte? Beim Aufwärmen nicht: es hängt an keinem Konto. */
export const zeigtKontenzeile = (kanal: string | null | undefined): boolean => kanal !== 'li_aufwaermen';

/** Aufwärm-Karte als Rezept (ADR 0353): drei Zeilen, immer nur die nächste ist aktiv. Die Häkchen sind eine
 *  Erinnerung für die Hilfe, kein Beweis; gespeichert wird erst „Erledigt“ nach der letzten Zeile. */
export const REZEPT_ZEILEN = [
  { titel: 'Seite öffnen', hilfe: 'öffnet LinkedIn in einem neuen Fenster' },
  { titel: 'Auf „Folgen“ tippen', hilfe: 'oben auf der Seite, dieser Knopf:' },
  { titel: '2 Beiträge liken', hilfe: 'runterscrollen, auf den Daumen tippen' },
] as const;

export type RezeptStand = 'fertig' | 'aktiv' | 'zu';

export const rezeptStand = (zeile: number, erledigt: number): RezeptStand =>
  zeile < erledigt ? 'fertig' : zeile === erledigt ? 'aktiv' : 'zu';

export const rezeptFertig = (erledigt: number): boolean => erledigt >= REZEPT_ZEILEN.length;

/** Detektiv-Runde (ADR 0353): eingefügter Text als Firmenlink, auch ohne „https://“ (so steht er oft im Kopf
 *  der Suche). Kein Firmenlink → null. */
export function firmenlinkAusEingabe(text: string | null | undefined): string | null {
  const t = (text ?? '').trim();
  if (!t) return null;
  const mitSchema = /^https?:\/\//i.test(t) ? t : `https://${t.replace(/^\/+/, '')}`;
  return istFirmenlink(mitSchema) ? mitSchema : null;
}

/** Schlüssel für „heute aufgewärmt“ im Browser, je Kalendertag (Ortszeit). Nur Ansporn, keine Zahl, die zählt. */
export const aufwaermHeuteSchluessel = (d: Date): string =>
  `aufwaermen-heute-${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Stand, den das Seitenpanel der Erweiterung zur Aufwärm-Karte meldet (ADR 0361). */
export interface AufwaermStandExtern {
  lead_id: number; offen: boolean; gefolgt: boolean; likes: number; firma_url: string | null; fertig: boolean;
  /** Wie viele Likes nötig sind: 2, oder weniger, wenn die Firma weniger Beiträge hat (Lida sagt es im Panel). */
  ziel?: number;
}

/** Wie viele Rezept-Zeilen der Stand aus dem Panel abhakt. Der Reihe nach: Seite offen, gefolgt, 2 Likes. */
export function rezeptAusPanel(s: Pick<AufwaermStandExtern, 'offen' | 'gefolgt' | 'likes' | 'ziel'>): number {
  if (!s.offen) return 0;
  if (!s.gefolgt) return 1;
  return s.likes >= (s.ziel ?? 2) ? 3 : 2;
}

/** Ist die Nachricht ein Stand aus dem Panel? Kommt über `window.postMessage` vom Inhaltsskript der Erweiterung. */
export const istPanelStand = (d: unknown): d is { quelle: 'agos'; typ: 'aufwaermen_stand'; stand: AufwaermStandExtern } =>
  !!d && typeof d === 'object' && (d as { quelle?: unknown }).quelle === 'agos'
  && (d as { typ?: unknown }).typ === 'aufwaermen_stand'
  && typeof (d as { stand?: { lead_id?: unknown } }).stand?.lead_id === 'number';

/** Was die Anfrage-Karte dem Seitenpanel übergibt (ADR 0366): wer, welche Firma, welches Konto, wohin. `von_panel`
 *  heißt, der Schritt kam nach einem Knopf im Panel; dann öffnet das Panel das Profil selbst im LinkedIn-Tab. */
export interface AnfrageLadung {
  kanal: 'li_anfrage'; lead_id: number; person_key: string | null; person_name: string | null; domain: string;
  linkedin_url: string | null; konto_id: number | null; konto_name: string | null; von_panel: boolean;
}

export function anfrageLadung(s: Pick<ArbeitslisteSchritt, 'lead_id' | 'person_key' | 'person_name' | 'domain' | 'linkedin_url'>,
                              kontoId: number | null, kontoName: string | null, vonPanel: boolean): AnfrageLadung {
  return {
    kanal: 'li_anfrage', lead_id: s.lead_id, person_key: s.person_key ?? null, person_name: s.person_name ?? null,
    domain: s.domain ?? '', linkedin_url: s.linkedin_url ?? null, konto_id: kontoId, konto_name: kontoName,
    von_panel: vonPanel,
  };
}

/** Ladung für das Panel bei „Prüfen: angenommen?“ (ADR 0412, Frage 32 A): Person, Firma und Konto der Karte. */
export interface AnnahmeLadung {
  kanal: 'li_annahme_pruefen'; lead_id: number; person_key: string | null; person_name: string | null; domain: string;
  linkedin_url: string | null; konto_name: string | null; von_panel: boolean;
}

export function annahmeLadung(s: Pick<ArbeitslisteSchritt, 'lead_id' | 'person_key' | 'person_name' | 'domain' | 'linkedin_url'>,
                              kontoName: string | null, vonPanel: boolean): AnnahmeLadung {
  return {
    kanal: 'li_annahme_pruefen', lead_id: s.lead_id, person_key: s.person_key ?? null, person_name: s.person_name ?? null,
    domain: s.domain ?? '', linkedin_url: s.linkedin_url ?? null, konto_name: kontoName, von_panel: vonPanel,
  };
}

/** Ein Knopf aus dem Panel für die Karte (ADR 0366, 0412): bei der Anfrage „gesendet“ oder „vernetzt“, bei „Annahme
 *  prüfen“ „vernetzt“ (= angenommen), „noch_offen“, „geghostet“ oder „blockiert“, für genau diese Firma. */
export type PanelAktion = 'gesendet' | 'vernetzt' | 'noch_offen' | 'geghostet' | 'blockiert';
export const PANEL_AKTIONEN: readonly PanelAktion[] = ['gesendet', 'vernetzt', 'noch_offen', 'geghostet', 'blockiert'];
export const istPanelAktion = (d: unknown): d is { quelle: 'agos'; typ: 'arbeitsliste_aktion'; aktion: PanelAktion; lead_id: number } =>
  !!d && typeof d === 'object' && (d as { quelle?: unknown }).quelle === 'agos'
  && (d as { typ?: unknown }).typ === 'arbeitsliste_aktion'
  && PANEL_AKTIONEN.includes((d as { aktion?: PanelAktion }).aktion as PanelAktion)
  && typeof (d as { lead_id?: unknown }).lead_id === 'number';

/** „Geführt“ (ADR 0412): die Firma, die als Karte dran ist. Die erste Zeile, die nicht bei jemand anderem in Arbeit ist
 *  und die in dieser Sitzung nicht zurückgelegt wurde; die Reihenfolge bestimmt der Server (L-1, kein zweiter Bauer). */
export function gefuehrteZeile<T extends { lead_id: number; in_arbeit: boolean; meins: boolean }>(
  zeilen: T[], zurueckgelegt: ReadonlySet<number>,
): T | null {
  return zeilen.find((z) => !zurueckgelegt.has(z.lead_id) && !(z.in_arbeit && !z.meins)) ?? null;
}

// ── Ansicht „Eine Firma nach der anderen“ (ADR 0364) ─────────────────────────────────────────────

/** Berliner Tag als Schlüssel; „Nächste Firma“ gilt nur für diesen Tag. */
export function parkSchluessel(jetzt: number): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Berlin' }).format(new Date(jetzt));
}

const TERMIN_GRUENDE = new Set(['anruf:rueckruf', 'anruf:wiedervorlage', 'anruf:erreicht']);

/**
 * Reihenfolge der Ansicht „Eine Firma“: fällige feste Termine von heute zuerst (nach Uhrzeit), dann die Reihenfolge des
 * Servers, dann die mit „Nächste Firma“ weggelegten (in der Reihenfolge des Weglegens). Nur die Anzeige; am Server
 * ändert sich nichts.
 */
export function reihenfolgeEineFirma<T extends { lead_id: number; grund?: string | null; faellig_am: string | null }>(
  zeilen: T[], hinten: number[], jetzt: number,
): T[] {
  const geparkt = new Set(hinten);
  const faelligTermin = (z: T) => !!z.grund && TERMIN_GRUENDE.has(z.grund) && !!z.faellig_am
    && Date.parse(z.faellig_am) <= jetzt;
  const offen = zeilen.filter((z) => !geparkt.has(z.lead_id));
  const termine = offen.filter(faelligTermin)
    .sort((a, b) => Date.parse(a.faellig_am as string) - Date.parse(b.faellig_am as string));
  const rest = offen.filter((z) => !faelligTermin(z));
  const weg = hinten.map((id) => zeilen.find((z) => z.lead_id === id)).filter((z): z is T => !!z);
  return [...termine, ...rest, ...weg];
}

// ── LinkedIn-Modus (ADR 0396): Konto-Wahl, Schild, Umschalter ────────────────────────────────────

interface KontoLage {
  id: number; name: string | null; offen: number; voll: boolean;
  schild: { meins: boolean; name: string; seit: string } | null;
}

/** `frei`: nehmbar, `meins`: das eigene Schild, `belegt`: ein anderer arbeitet gerade darin. */
export function kontoStand(k: Pick<KontoLage, 'schild'>): 'frei' | 'meins' | 'belegt' {
  if (!k.schild) return 'frei';
  return k.schild.meins ? 'meins' : 'belegt';
}

/** „belegt von Lida seit 10:12“ (Uhrzeit in Berlin). */
export function schildText(s: { name: string; seit: string }): string {
  const t = new Date(s.seit);
  const uhr = Number.isNaN(t.getTime()) ? '' : ` seit ${new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit',
  }).format(t)}`;
  return `belegt von ${s.name}${uhr}`;
}

/** Hinweis, wenn das gewählte Konto nichts mehr annimmt (ADR 0383: immer mit Grund). `null`: alles gut. */
export function kontoVollHinweis(k: Pick<KontoLage, 'name' | 'voll'>): string | null {
  return k.voll ? `${k.name ?? 'Das Konto'} ist für heute voll. Aufwärmen geht weiter, Anfragen nicht.` : null;
}

/** Ein freies, nicht volles Konto zum Vorschlagen, wenn das eigene voll ist (das mit den meisten offenen Schritten). */
export function freiesKonto<T extends KontoLage>(konten: T[], ausser: number | null): T | null {
  const frei = konten.filter((k) => k.id !== ausser && !k.voll && kontoStand(k) === 'frei' && k.offen > 0);
  return frei.sort((a, b) => b.offen - a.offen)[0] ?? null;
}

/** Nach „erledigt“ klappt die nächste Zeile auf (Frage 15): die erste, die weder erledigt noch von anderen gehalten ist. */
export function naechsteOffeneZeile<T extends { lead_id: number; in_arbeit: boolean; meins: boolean }>(
  zeilen: T[], erledigt: number | null,
): number | null {
  const frei = zeilen.filter((z) => z.lead_id !== erledigt && (!z.in_arbeit || z.meins));
  return frei[0]?.lead_id ?? null;
}
